import { downloadMessageMediaFile } from "@/components/ui/CommunicationSlideover/downloadMessageMediaFile";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { downloadFileLink } from "@/lib/utils/matterFile";
import { Download } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

export const CommunicationVideo = ({
  s3Key,
  url,
  onRetry,
  isRefreshing,
}: {
  s3Key: string;
  url: string;
  onRetry: () => Promise<boolean>;
  isRefreshing: boolean;
}) => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const download = async () => {
    setIsDownloading(true);
    try {
      const file = await downloadMessageMediaFile(s3Key);
      const objectUrl = URL.createObjectURL(file);
      downloadFileLink(file.name, objectUrl);
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch {
      toast.error("Unable to download this video. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-muted/30 w-72 max-w-full overflow-hidden rounded-md border">
      {failedUrl === url ? (
        <div className="flex min-h-40 flex-col items-center justify-center gap-3 p-4 text-center">
          <p role="alert" className="text-sm">
            Unable to play this video. Try again or download it to watch on your
            device.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isRefreshing}
            onClick={async () => {
              const result = await onRetry();
              if (result) {
                setFailedUrl(null);
                setAttempt((value) => value + 1);
              }
            }}
          >
            Try again
          </Button>
        </div>
      ) : (
        <video
          key={`${url}:${attempt}`}
          src={url}
          controls
          playsInline
          preload="metadata"
          aria-label="Text message video attachment"
          className="max-h-80 w-full bg-black"
          onError={() => {
            setFailedUrl(url);
          }}
        />
      )}
      <div className="flex justify-end p-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isDownloading}
          onClick={() => void download()}
        >
          <Download className="mr-2 h-4 w-4" />
          {isDownloading ? "Downloading…" : "Download video"}
        </Button>
      </div>
    </div>
  );
};
